import type { IUser } from "@/lib/types";
import { io, Socket } from "socket.io-client";
import { tokenRefreshManager } from "@/lib/tokenRefresh";

// Socket.io events
export const SocketEvents = {
  // Connection events
  CONNECT: 'connect',
  DISCONNECT: 'disconnect',
  ERROR: 'error',
  RECONNECT: 'reconnect',
  RECONNECT_ATTEMPT: 'reconnect_attempt',
  RECONNECT_ERROR: 'reconnect_error',
  RECONNECT_FAILED: 'reconnect_failed',

  // Auth lifecycle events (server proactively warns/disconnects on JWT expiry)
  AUTH_TOKEN_EXPIRING: 'auth:token_expiring',
  AUTH_TOKEN_EXPIRED: 'auth:token_expired',
  AUTH_TOKEN_REFRESHED: 'auth:token_refreshed',
  AUTH_ERROR: 'auth:error',

  // Email events
  EMAIL_RECEIVED: 'email:received',
  EMAIL_UPDATED: 'email:updated',
  EMAIL_DELETED: 'email:deleted',
  EMAIL_READ: 'email:read',
  EMAIL_STARRED: 'email:starred',
  EMAIL_LABELED: 'email:labeled',
  EMAIL_MOVED: 'email:moved',

  // User events
  USER_ONLINE: 'user:online',
  USER_OFFLINE: 'user:offline',
  USER_TYPING: 'user:typing',

  // Notification events
  NOTIFICATION: 'notification',
  MESSAGE_NOTIFICATION: 'message:notification',
  CALL_INVITATION: 'call:invitation',

  // Snooze & follow-up events
  EMAIL_UNSNOOZED: 'email_unsnoozed',
  EMAIL_REMINDER: 'email_reminder',

  // Import progress events
  IMPORT_START: 'import:start',
  IMPORT_PROGRESS: 'import:progress',
  IMPORT_FOLDER_DONE: 'import:folder-done',
  IMPORT_DONE: 'import:done',
} as const;

// Socket.io service for real-time communication
class SocketService {
  private socket: Socket | null = null;
  private listeners: Map<string, Set<Function>> = new Map();
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private user: IUser | null = null;

  // Initialize socket connection
  public async init(user: IUser): Promise<void> {
    if (this.socket) {
      console.warn("Socket connection already initialized", this.reconnectAttempts, this.user);
      return;
    }

    this.user = user;

    // Connect to the socket server. `auth` is a function (not a plain
    // object) so it's re-invoked before every connection AND reconnection
    // attempt, always fetching a live token via the same refresh manager
    // the REST layer uses — a plain object would freeze in the token
    // captured at the very first connect, which the server rejects once
    // that token expires, silently killing real-time delivery until a
    // full page reload creates a brand-new socket.
    this.socket = io(import.meta.env.VITE_SOCKET_URL || "http://localhost:5000", {
      reconnectionAttempts: this.maxReconnectAttempts,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 20000,
      auth: (cb) => {
        tokenRefreshManager
          .getValidAccessToken()
          .then((token) => cb({ token, userId: user.id }))
          .catch(() => cb({ token: null, userId: user.id }));
      },
    });

    // Set up event listeners
    this.setupEventListeners();
  }

  // Set up default event listeners
  private setupEventListeners(): void {
    if (!this.socket) return;

    this.socket.on(SocketEvents.CONNECT, () => {
      console.log("Socket connected");
      this.reconnectAttempts = 0;
      this.emitEvent(SocketEvents.CONNECT, null);
    });

    this.socket.on(SocketEvents.DISCONNECT, (reason) => {
      console.log(`Socket disconnected: ${reason}`);
      this.emitEvent(SocketEvents.DISCONNECT, reason);
    });

    this.socket.on(SocketEvents.ERROR, (error) => {
      console.error("Socket error:", error);
      this.emitEvent(SocketEvents.ERROR, error);
    });

    this.socket.on(SocketEvents.RECONNECT_ATTEMPT, (attempt) => {
      console.log(`Socket reconnection attempt ${attempt}`);
      this.reconnectAttempts = attempt;
      this.emitEvent(SocketEvents.RECONNECT_ATTEMPT, attempt);
    });

    this.socket.on(SocketEvents.RECONNECT, (attempt) => {
      console.log(`Socket reconnected after ${attempt} attempts`);
      this.reconnectAttempts = 0;
      this.emitEvent(SocketEvents.RECONNECT, attempt);
    });

    this.socket.on(SocketEvents.RECONNECT_ERROR, (error) => {
      console.error("Socket reconnection error:", error);
      this.emitEvent(SocketEvents.RECONNECT_ERROR, error);
    });

    this.socket.on(SocketEvents.RECONNECT_FAILED, () => {
      console.error("Socket reconnection failed");
      this.emitEvent(SocketEvents.RECONNECT_FAILED, null);
    });

    // Server warns 5 min before the JWT backing this connection expires.
    // Proactively refresh and hand the new token to the server so the
    // existing connection stays alive — no disconnect/reconnect needed.
    this.socket.on(SocketEvents.AUTH_TOKEN_EXPIRING, async () => {
      try {
        const token = await tokenRefreshManager.refreshAccessToken();
        this.socket?.emit("presence:token_refresh", { token });
      } catch (error) {
        console.error("Failed to refresh token ahead of socket expiry:", error);
      }
    });

    // Fallback: the server disconnected us because the token already
    // expired (e.g. tab was backgrounded through the warning window).
    // `auth` is re-evaluated on every reconnect attempt, so a plain
    // reconnect now picks up a fresh token automatically.
    this.socket.on(SocketEvents.AUTH_TOKEN_EXPIRED, () => {
      console.warn("Socket auth token expired — reconnecting with a fresh token");
      this.socket?.connect();
    });

    this.socket.on(SocketEvents.AUTH_ERROR, (error) => {
      console.error("Socket auth error:", error);
      this.emitEvent(SocketEvents.AUTH_ERROR, error);
    });

    // Attach any existing listeners (subscribed before connection)
    this.listeners.forEach((_, event) => {
      if (!this.isDefaultEvent(event) && this.socket) {
        // Remove existing listeners to avoid duplicates if any (though socket is new)
        this.socket.off(event);
        this.socket.on(event, (data: any) => {
          this.emitEvent(event, data);
        });
      }
    });
  }

  // Subscribe to an event
  public on(event: string, callback: Function): () => void {
    const isNewEvent = !this.listeners.has(event);
    if (isNewEvent) {
      this.listeners.set(event, new Set());
    }

    const eventListeners = this.listeners.get(event);
    if (eventListeners) {
      eventListeners.add(callback);
    }

    // Add socket listener ONLY IF it's the first time we subscribe to this event
    if (isNewEvent && this.socket && !this.isDefaultEvent(event)) {
      this.socket.on(event, (data: any) => {
        this.emitEvent(event, data);
      });
    }

    // Return unsubscribe function
    return () => {
      const eventListeners = this.listeners.get(event);
      if (eventListeners) {
        eventListeners.delete(callback);
        // If no more listeners for this event, remove from socket
        if (eventListeners.size === 0 && this.socket && !this.isDefaultEvent(event)) {
          this.socket.off(event);
          this.listeners.delete(event);
        }
      }
    };
  }

  // Emit an event to the server
  public emit(event: string, data: any): void {
    if (!this.socket) {
      console.warn("Socket not initialized");
      return;
    }

    this.socket.emit(event, data);
  }

  // Emit an event to all listeners
  private emitEvent(event: string, data: any): void {
    if (event.startsWith("import:")) {
      console.log(`[Socket] Received event: ${event}`, data);
    }
    const eventListeners = this.listeners.get(event as keyof typeof SocketEvents);
    if (eventListeners) {
      eventListeners.forEach((callback) => {
        try {
          callback(data);
        } catch (error) {
          console.error(`Error in ${event} listener:`, error);
        }
      });
    }
  }

  // Check if an event is a default socket.io event (already handled in setupEventListeners)
  private isDefaultEvent(event: string): boolean {
    return [
      SocketEvents.CONNECT,
      SocketEvents.DISCONNECT,
      SocketEvents.ERROR,
      SocketEvents.RECONNECT,
      SocketEvents.RECONNECT_ATTEMPT,
      SocketEvents.RECONNECT_ERROR,
      SocketEvents.RECONNECT_FAILED,
      SocketEvents.AUTH_TOKEN_EXPIRING,
      SocketEvents.AUTH_TOKEN_EXPIRED,
      SocketEvents.AUTH_ERROR,
    ].includes(event as any);
  }

  // Disconnect socket
  public disconnect(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.listeners.clear();
      this.user = null;
    }
  }

  // Check if socket is connected
  public isConnected(): boolean {
    return this.socket?.connected || false;
  }
}

// Export as singleton
export const socketService = new SocketService();
